export class Autocomplete {
	constructor(inputElement, config) {
		this.textBox = inputElement;
		this.container = this.textBox.parentElement;

		// Configuration
		this.fetchOptions = config.fetchOptions;
		this.minLength = config.minLength || 3;
		this.debounceTime = config.debounceTime || 300;
		this.hiddenInputName =
			config.hiddenInputName || `${this.textBox.name}_value`;

		// State
		this.activeOptionId = null;
		this.debounceTimeout = null;
		this.latestRequestId = 0; // Protects against race conditions

		this.setupDOM();
		this.setupKeys();

		document.addEventListener("click", this.onDocumentClick.bind(this));
	}

	setupDOM() {
		// Create wrapper
		this.wrapper = document.createElement("div");
		this.wrapper.classList.add("fn-autocomplete");

		// Wrap the existing input
		this.container.insertBefore(this.wrapper, this.textBox);
		this.wrapper.appendChild(this.textBox);

		// Create a hidden input to store the actual selected value for form submission
		this.hiddenInput = document.createElement("input");
		this.hiddenInput.type = "hidden";
		this.hiddenInput.name = this.hiddenInputName;
		this.wrapper.appendChild(this.hiddenInput);

		this.setupTextBox();
		// this.createArrowIcon();
		this.createMenu();
		this.createStatusBox();
	}

	setupKeys() {
		this.keys = {
			enter: 13,
			esc: 27,
			space: 32,
			up: 38,
			down: 40,
			tab: 9,
			left: 37,
			right: 39,
			shift: 16,
		};
	}

	setupTextBox() {
		this.textBox.setAttribute("autocapitalize", "none");
		this.textBox.setAttribute("autocomplete", "off");
		this.textBox.setAttribute("aria-owns", this.getOptionsId());
		this.textBox.setAttribute("aria-autocomplete", "list");
		this.textBox.setAttribute("role", "combobox");

		this.textBox.addEventListener("click", this.onTextBoxClick.bind(this));
		this.textBox.addEventListener("keydown", (e) => {
			if (e.keyCode === this.keys.tab) {
				this.hideMenu();
				this.removeTextBoxFocus();
			}
		});
		this.textBox.addEventListener("keyup", this.onTextBoxKeyUp.bind(this));
		this.textBox.addEventListener("focus", this.onTextBoxFocus.bind(this));
	}

	onDocumentClick(e) {
		if (!this.wrapper.contains(e.target)) {
			this.hideMenu();
			this.removeTextBoxFocus();
		}
	}

	onTextBoxFocus() {
		this.textBox.classList.add("autocomplete-isFocused");
	}

	removeTextBoxFocus() {
		this.textBox.classList.remove("autocomplete-isFocused");
	}

	onTextBoxClick(e) {
		if (
			this.textBox.value.trim().length >= this.minLength &&
			this.menu.children.length > 0
		) {
			this.showMenu();
		}
		if (typeof e.currentTarget.select === "function") {
			e.currentTarget.select();
		}
	}

	onTextBoxKeyUp(e) {
		switch (e.keyCode) {
			case this.keys.esc:
			case this.keys.up:
			case this.keys.left:
			case this.keys.right:
			case this.keys.space:
			case this.keys.enter:
			case this.keys.tab:
			case this.keys.shift:
				break; // Ignore navigation/selection keys for fetching
			case this.keys.down:
				this.onTextBoxDownPressed(e);
				break;
			default:
				this.onTextBoxType();
		}
	}

	onTextBoxType() {
		const val = this.textBox.value.trim();

		// Clear user selection if they alter the text
		this.hiddenInput.value = "";

		// Clear existing debounce timer
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
		}

		if (val.length >= this.minLength) {
			this.status.textContent = "Loading results...";

			// Start the debounce timer
			this.debounceTimeout = setTimeout(async () => {
				const requestId = ++this.latestRequestId; // Increment request tracker

				try {
					const options = await this.fetchOptions(val);

					// Only render if this is still the most recent request
					if (requestId === this.latestRequestId) {
						this.buildMenu(options);
						this.showMenu();
						this.updateStatus(options.length);
					}
				} catch (error) {
					if (requestId === this.latestRequestId) {
						console.error("Autocomplete fetch error:", error);
						this.clearOptions();
						this.status.textContent = "Error loading results.";
						this.hideMenu();
					}
				}
			}, this.debounceTime);
		} else {
			// Input is too short, hide and clear
			this.hideMenu();
			this.clearOptions();
			this.status.textContent = "";
		}
	}

	onMenuKeyDown(e) {
		switch (e.keyCode) {
			case this.keys.up:
				this.onOptionUpArrow(e);
				break;
			case this.keys.down:
				this.onOptionDownArrow(e);
				break;
			case this.keys.enter:
				this.onOptionEnter(e);
				break;
			case this.keys.space:
				this.onOptionSpace(e);
				break;
			case this.keys.esc:
				this.onOptionEscape(e);
				break;
			case this.keys.tab:
				this.hideMenu();
				this.removeTextBoxFocus();
				break;
			default:
				this.textBox.focus();
		}
	}

	onOptionEscape() {
		this.hideMenu();
		this.focusTextBox();
	}

	focusTextBox() {
		this.textBox.focus();
	}

	onOptionEnter(e) {
		if (this.isOptionSelected()) {
			this.selectActiveOption();
		}
		e.preventDefault();
	}

	onOptionSpace(e) {
		if (this.isOptionSelected()) {
			this.selectActiveOption();
			e.preventDefault();
		}
	}

	onOptionClick(e) {
		const option = e.currentTarget;
		this.selectOption(option);
	}

	selectActiveOption() {
		const option = this.getActiveOption();
		if (option) this.selectOption(option);
	}

	selectOption(option) {
		const value = option.getAttribute("data-option-value");
		const text = option.textContent;

		// Update both inputs
		this.textBox.value = text;
		this.hiddenInput.value = value;

		this.hideMenu();
		this.focusTextBox();
	}

	onTextBoxDownPressed() {
		// If menu is open, navigate into it
		if (!this.menu.hasAttribute("hidden") && this.menu.children.length > 0) {
			const option = this.getFirstOption();
			if (option) this.highlightOption(option);
		}
	}

	onOptionDownArrow(e) {
		const option = this.getNextOption();
		if (option) {
			this.highlightOption(option);
		}
		e.preventDefault();
	}

	onOptionUpArrow(e) {
		if (this.isOptionSelected()) {
			const option = this.getPreviousOption();
			if (option) {
				this.highlightOption(option);
			} else {
				this.focusTextBox();
				this.activeOptionId = null;

				// Ensure previously highlighted item is un-highlighted
				const oldActive = this.menu.querySelector('[aria-selected="true"]');
				if (oldActive) oldActive.setAttribute("aria-selected", "false");
			}
		}
		e.preventDefault();
	}

	isOptionSelected() {
		return this.activeOptionId !== null;
	}

	getActiveOption() {
		return this.getOptionById(this.activeOptionId);
	}

	getFirstOption() {
		return this.menu.querySelector("li");
	}

	getPreviousOption() {
		const active = this.getActiveOption();
		return active ? active.previousElementSibling : null;
	}

	getNextOption() {
		const active = this.getActiveOption();
		return active ? active.nextElementSibling : null;
	}

	highlightOption(option) {
		if (this.activeOptionId) {
			const activeOption = this.getOptionById(this.activeOptionId);
			if (activeOption) activeOption.setAttribute("aria-selected", "false");
		}

		option.setAttribute("aria-selected", "true");

		if (!this.isElementVisible(this.menu, option)) {
			this.menu.scrollTop = option.offsetTop;
		}

		this.activeOptionId = option.id;
		option.focus();
	}

	getOptionById(id) {
		return id ? this.menu.querySelector(`#${CSS.escape(id)}`) : null;
	}

	showMenu() {
		this.menu.removeAttribute("hidden");
		this.textBox.setAttribute("aria-expanded", "true");
	}

	hideMenu() {
		this.menu.setAttribute("hidden", "hidden");
		this.textBox.setAttribute("aria-expanded", "false");
		this.activeOptionId = null;
	}

	clearOptions() {
		this.menu.innerHTML = "";
	}

	buildMenu(options) {
		this.clearOptions();
		this.activeOptionId = null;

		if (options.length) {
			options.forEach((opt, i) => {
				this.menu.insertAdjacentHTML("beforeend", this.getOptionHtml(i, opt));
			});
		} else {
			this.menu.insertAdjacentHTML("beforeend", this.getNoResultsOptionHtml());
		}

		// Attach event listeners
		const listItems = this.menu.querySelectorAll('[role="option"]');
		listItems.forEach((li) => {
			li.addEventListener("click", this.onOptionClick.bind(this));
		});

		this.menu.scrollTop = 0;
	}

	getNoResultsOptionHtml() {
		return '<li class="autocomplete-optionNoResults" tabindex="-1">No results found</li>';
	}

	getOptionHtml(i, option) {
		// Using a random string or input ID to ensure uniqueness if multiple components exist
		const baseId =
			this.textBox.id || Math.random().toString(36).substring(2, 9);
		return `<li tabindex="-1" aria-selected="false" role="option" data-option-value="${option.value}" id="autocomplete-option-${baseId}-${i}">${option.text}</li>`;
	}

	createStatusBox() {
		this.status = document.createElement("div");
		this.status.setAttribute("aria-live", "polite");
		this.status.setAttribute("role", "status");
		this.status.className = "visually-hidden";
		this.wrapper.appendChild(this.status);
	}

	updateStatus(resultCount) {
		if (resultCount === 0) {
			this.status.textContent = "No results found.";
		} else {
			this.status.textContent = `${resultCount} results available. Use up and down arrows to navigate.`;
		}
	}

	getOptionsId() {
		return `autocomplete-options--${this.textBox.id || "default"}`;
	}

	createArrowIcon() {
		const svgNS = "http://www.w3.org/2000/svg";
		const arrow = document.createElementNS(svgNS, "svg");
		arrow.setAttribute("focusable", "false");
		arrow.setAttribute("version", "1.1");

		const g = document.createElementNS(svgNS, "g");
		const polygon = document.createElementNS(svgNS, "polygon");
		polygon.setAttribute("points", "0 0 22 0 11 17");

		g.appendChild(polygon);
		arrow.appendChild(g);

		this.wrapper.appendChild(arrow);

		arrow.addEventListener("click", () => {
			// Since it's async, we can't just "show all" easily without an explicit fetchAll method.
			// We'll just focus the text box and show existing results if they exist.
			this.focusTextBox();
			if (this.menu.children.length > 0) {
				this.showMenu();
			}
		});
	}

	createMenu() {
		this.menu = document.createElement("ul");
		this.menu.id = this.getOptionsId();
		this.menu.setAttribute("role", "listbox");
		this.menu.setAttribute("hidden", "hidden");

		this.wrapper.appendChild(this.menu);
		this.menu.addEventListener("keydown", this.onMenuKeyDown.bind(this));
	}

	isElementVisible(container, element) {
		const cRect = container.getBoundingClientRect();
		const eRect = element.getBoundingClientRect();
		return eRect.top >= cRect.top && eRect.bottom <= cRect.bottom;
	}
}
